import React, { useEffect, useRef } from "react";
import { ensureAdSenseScript, getAdSenseClient } from "@/lib/ads";

/**
 * Passive manual display unit. Renders nothing unless client + slot are set.
 * Reserved min-height only when visible (avoids CLS once ads are enabled).
 * No sticky / anchored / interstitial behavior.
 */
export function AdSlot({
  slot,
  className = "",
  minHeight = 90,
  format = "horizontal",
  testId = "ad-slot",
}) {
  const client = getAdSenseClient();
  const enabled = Boolean(client && slot);
  const pushedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;
    ensureAdSenseScript(client);
    return undefined;
  }, [enabled, client]);

  useEffect(() => {
    if (!enabled || pushedRef.current) return undefined;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushedRef.current = true;
    } catch {
      /* AdSense may throw if blocked or not ready; fail quietly */
    }
    return undefined;
  }, [enabled, client, slot]);

  if (!enabled) return null;

  return (
    <aside
      className={`w-full overflow-hidden border border-[#0A0A0A]/12 bg-[#FAFAFA] ${className}`}
      style={{ minHeight }}
      aria-label="Advertisement"
      data-testid={testId}
    >
      <div className="px-2 pt-1 text-[9px] uppercase tracking-[0.25em] text-[#A1A1AA] font-mono-data select-none">
        Ad
      </div>
      <ins
        className="adsbygoogle"
        style={{ display: "block", minHeight: Math.max(minHeight - 18, 50) }}
        data-ad-client={client}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </aside>
  );
}
